/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { companyLogoItem, pageStructureStyles } from "@eisinc/App/SharedStyles/GeneralStyles";
import { screen } from "@eisinc/XDL/Helpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { PageErrorWrapper } from "@insite/client-framework/Components/PageErrorWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getInvoiceState, InvoiceStateContext } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import { getOrderStatusMappingDataView } from "@insite/client-framework/Store/Data/OrderStatusMappings/OrderStatusMappingsSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import displayInvoice from "@insite/client-framework/Store/Pages/InvoiceDetails/Handlers/DisplayInvoice";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import { GridItemProps } from "@insite/mobius/GridItem";
import { GridContainer, GridItem } from "@insite/mobius/index";
import Page from "@insite/mobius/Page";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);

    let invoiceNumber;

    if (location && location.search) {
        const parsedQuery = parseQueryString<{ invoiceNumber: string }>(location.search);

        invoiceNumber = parsedQuery.invoiceNumber;
    }

    const isInvoiceNumberStoredInState = state.pages.invoiceDetails.invoiceNumber === invoiceNumber;

    return {
        invoiceNumber,
        invoiceState: getInvoiceState(state, invoiceNumber),
        shouldDisplayInvoice: !isInvoiceNumberStoredInState,
        shouldLoadOrderStatusMappings: !getOrderStatusMappingDataView(state).value,
    };
};

const mapDispatchToProps = {
    displayInvoice,
};

export interface InvoiceDetailsPageStyles {
    loadFailedWrapper?: InjectableCss;
    loadFailedText?: TypographyPresentationProps;
    messageGridItem?: GridItemProps;
    infoGridItem?: GridItemProps;
    repInfoGridItem?: GridItemProps;
    invoiceSummaryGridItem?: GridItemProps;
    notesGridItem?: GridItemProps;
    orderLines?: GridItemProps;
}

export const invoiceDetailsPageStyles: InvoiceDetailsPageStyles = {
    loadFailedWrapper: {
        css: css`
            display: flex;
            height: 200px;
            justify-content: center;
            align-items: center;
            background-color: ${getColor("common.accent")};
        `,
    },
    loadFailedText: {
        weight: "bold",
    },
    messageGridItem: {
        width: 12,
    },
    infoGridItem: {
        width: 12,
    },
    repInfoGridItem: {
        width: [12, 12, 6, 12, 12],
    },
    invoiceSummaryGridItem: {
        width: [12, 12, 6, 12, 12],
    },
    notesGridItem: {
        width: 12,
    },
    orderLines: {
        css: css`
            @media screen {
                margin-top: 30px;
            }

            ${screen("gte", "lg")`
                width: 66.666667%;
            `}
        `,
    },
};

type Props = PageProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

class InvoiceDetailsPage extends React.Component<Props> {
    UNSAFE_componentWillMount(): void {
        // The share entity (for orders) functionality can use this page
        // to render HTML for PDF generation, so we need to leave this SSR
        // ability so that functionality works.
        if (this.props.invoiceNumber && this.props.shouldDisplayInvoice) {
            this.props.displayInvoice({ invoiceNumber: this.props.invoiceNumber });
        }
    }

    render() {
        const styles = invoiceDetailsPageStyles;

        return (
            <Page>
                <PageErrorWrapper
                    value={this.props.invoiceState}
                    extendedStyles={styles}
                    notFoundMessage={siteMessage("InvoiceHistory_Not_Found")}
                >
                    <InvoiceStateContext.Provider value={this.props.invoiceState}>
                        <GridContainer>
                            <GridItem {...pageStructureStyles.headerGridItem}>
                                <StyledWrapper {...companyLogoItem}>
                                    <img src={siteMessage("Company_Logo") as any} alt={translate("Company Logo")} />
                                </StyledWrapper>
                                <Zone contentId={this.props.id} zoneName="HeaderWidget" />
                            </GridItem>
                            <GridItem {...pageStructureStyles.contentOneGridItem}>
                                <GridContainer>
                                    <GridItem {...styles.messageGridItem}>
                                        <Zone contentId={this.props.id} zoneName="MessageRichContentWidget" />
                                    </GridItem>
                                    <GridItem {...styles.infoGridItem}>
                                        <Zone contentId={this.props.id} zoneName="InfoWidget" />
                                    </GridItem>
                                </GridContainer>
                            </GridItem>
                            <GridItem {...pageStructureStyles.contentTwoGridItem}>
                                <GridContainer>
                                    <GridItem {...styles.invoiceSummaryGridItem}>
                                        <Zone contentId={this.props.id} zoneName="InvoiceSummaryWidget" />
                                    </GridItem>
                                    <GridItem {...styles.repInfoGridItem}>
                                        <Zone contentId={this.props.id} zoneName="RepInfoWidget" />
                                    </GridItem>
                                    <GridItem {...styles.notesGridItem}>
                                        <Zone contentId={this.props.id} zoneName="NotesWidget" />
                                    </GridItem>
                                </GridContainer>
                            </GridItem>
                        </GridContainer>
                        <StyledWrapper {...styles.orderLines}>
                            <Zone contentId={this.props.id} zoneName="InvoiceLinesWidget" />
                        </StyledWrapper>
                    </InvoiceStateContext.Provider>
                </PageErrorWrapper>
            </Page>
        );
    }
}

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(InvoiceDetailsPage),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "System",
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "InvoiceDetailsPage" instead of this constant.
 */
export const InvoiceDetailsPageContext = "InvoiceDetailsPage";
